import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useEffect } from "react";
import { useState } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Swal from "sweetalert2";
import "./leavePolicies.css";

function LeaveAllocation() {
  const [permissions, setPermissions] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState({
    id: "",
    leave_type_id: "",
    branch_id: "",
    year: new Date().getFullYear(),
    max_days: 0,
    is_active: true,
    requires_document: false,
  });
  const [editLeaveAllocation, setEditLeaveAllocation] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [branches, setBranches] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [leaveAllocations, setLeaveAllocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("branch");
  const [branchFilter, setBranchFilter] = useState("all");
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const fetchLeaveAllocation = async () => {
    try {
      setShowSkeleton(true);
      const response = await apiUrl.get("/get_leave_allocations");
      if (response?.status) {
        setLeaveAllocations(response?.data?.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowSkeleton(false);
    }
  };

  useEffect(() => {
    const perms = getPermissions();
    if (perms) {
      setPermissions(perms);
    }
  }, []);

  useEffect(() => {
    fetchData("/getBranches", setBranches);
    fetchData("/get_leave_types", setLeaveTypes);
    fetchLeaveAllocation();
  }, []);

  // fetch data about branches and leave types
  const fetchData = async (endpoint, setter) => {
    setShowSkeleton(true);
    try {
      const response = await apiUrl.get(endpoint);
      if (response?.status) {
        setter(response?.data?.data || []);
      }
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
    } finally {
      setShowSkeleton(false);
    }
  };
  const handleAddNew = () => {
    setData({
      leave_type_id: "",
      branch_id: "",
      year: new Date().getFullYear(),
      max_days: 0,
      is_active: true,
      requires_document: false,
    });
    setOpenModal(true);
    setErrors({});
    setEditLeaveAllocation(false);
  };
  // skeleton row
  const SkeletonRow = () => (
    <tr>
      <td><div className="lp-skeleton" style={skeletonStyle(120)} /></td>
      <td><div className="lp-skeleton" style={skeletonStyle(100)} /></td>
      <td><div className="lp-skeleton mx-auto" style={skeletonStyle(60)} /></td>
      <td><div className="lp-skeleton mx-auto" style={skeletonStyle(80)} /></td>
      <td><div className="lp-skeleton mx-auto" style={skeletonStyle(70)} /></td>
      <td><div className="lp-skeleton mx-auto" style={skeletonStyle(40)} /></td>
    </tr>
  );

  // helper function for inline style
  const skeletonStyle = (width) => ({
    width: `${width}px`,
    height: "18px",
  });

  // handle form validation
  const validateForm = () => {
    const newErrors = {};
    if (!data?.leave_type_id)
      newErrors.leave_type_id = "Leave Type is required";
    if (!data?.branch_id) newErrors.branch_id = "Branch is required";
    if (!data?.max_days) newErrors.max_days = "Maximum Days is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // handle save document
  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      let response;

      if (editLeaveAllocation) {
        response = await apiUrl.post(`/update_leave_allocation/${data.id}`, {
          leave_type_id: data.leave_type_id,
          branch_id: data.branch_id,
          max_days: data.max_days,
          requires_document: data.requires_document,
          is_active: data.is_active,
        });
      } else {
        response = await apiUrl.post("/add_leave_allocation", {
          leave_type_id: data.leave_type_id,
          branch_id: data.branch_id,
          max_days: data.max_days,
          requires_document: data.requires_document,
          is_active: data.is_active,
        });
      }

      if (response?.status) {
        fetchLeaveAllocation();
        Swal.fire({
          title: response.data.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setOpenModal(false);
        setEditLeaveAllocation(false);
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: error.response?.data?.message || "Something went wrong",
        icon: "error",
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAllocation = (allocation) => {
    setOpenModal(true);
    setEditLeaveAllocation(true);
    setData({
      id: allocation.id,
      leave_type_id: allocation.leave_type_id,
      branch_id: allocation.branch_id,
      year: allocation.year,
      max_days: allocation.max_days,
      is_active: allocation.is_active,
      requires_document: allocation.requires_document,
    });
  };

  const activeCount = leaveAllocations.filter((a) => a.is_active).length;
  const docRequiredCount = leaveAllocations.filter((a) => a.requires_document).length;

  const filteredAllocations = leaveAllocations.filter((allocation) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      allocation?.leave_type_name?.toLowerCase().includes(term) ||
      allocation?.branch_name?.toLowerCase().includes(term)
    );
  });

  const branchFiltered = filteredAllocations.filter((a) =>
    branchFilter === "all" ? true : a.branch_name === branchFilter
  );

  const uniqueBranches = [
    ...new Set(leaveAllocations.map((a) => a.branch_name).filter(Boolean)),
  ].sort();

  const groupByKey = (items, keyFn) =>
    items.reduce((acc, item) => {
      const key = keyFn(item) || "Unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

  const branchGroups = groupByKey(branchFiltered, (a) => a.branch_name);
  const leaveTypeGroups = groupByKey(branchFiltered, (a) => a.leave_type_name);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderAllocationRow = (allocation) => (
    <div key={allocation.id} className="lp-alloc-row">
      <div className="lp-alloc-leave-name">
        {viewMode === "leave_type" ? (
          <>
            <FaIcons.FaMapMarkerAlt className="me-1 text-muted" size={11} />
            {allocation.branch_name}
          </>
        ) : (
          <>
            {allocation.leave_type_name}
            {viewMode === "all" && (
              <div className="text-muted small fw-normal">{allocation.branch_name}</div>
            )}
          </>
        )}
      </div>
      <span className="lp-days-badge">{allocation.max_days}d</span>
      {allocation.requires_document ? (
        <span className="badge lp-badge-soft-warning">Doc</span>
      ) : (
        <span className="badge lp-badge-soft-secondary">No Doc</span>
      )}
      {allocation.is_active ? (
        <span className="badge lp-badge-soft-success">Active</span>
      ) : (
        <span className="badge lp-badge-soft-danger">Inactive</span>
      )}
      <button
        onClick={() => handleEditAllocation(allocation)}
        className="btn btn-sm btn-outline-primary"
        title="Edit"
      >
        <FaIcons.FaPencilAlt />
      </button>
    </div>
  );

  const renderGroupedView = (groups, icon) =>
    Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([groupName, items]) => {
        const isCollapsed = collapsedGroups[groupName];
        const activeInGroup = items.filter((i) => i.is_active).length;
        return (
          <div key={groupName} className="lp-branch-group">
            <div
              className="lp-branch-group-header"
              onClick={() => toggleGroup(groupName)}
            >
              <div className="lp-branch-group-title">
                <span className="lp-branch-group-icon">{icon}</span>
                <div>
                  <div>{groupName}</div>
                  <span className="lp-branch-group-count">
                    {items.length} allocation{items.length !== 1 ? "s" : ""} · {activeInGroup} active
                  </span>
                </div>
              </div>
              <FaIcons.FaChevronDown
                style={{
                  transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  color: "#94a3b8",
                }}
              />
            </div>
            {!isCollapsed && (
              <div className="lp-branch-group-body">
                {items.map(renderAllocationRow)}
              </div>
            )}
          </div>
        );
      });

  return (
    <>
      {permissions["manage_policies"] === 1 && (
        <div>
          {!showSkeleton && leaveAllocations.length > 0 && (
            <div className="row g-3 mb-4">
              <div className="col-sm-4">
                <div className="lp-stat-card d-flex align-items-center gap-3">
                  <div className="lp-stat-icon bg-primary bg-opacity-10 text-primary">
                    <FaIcons.FaLayerGroup />
                  </div>
                  <div>
                    <div className="text-muted small">Total Allocations</div>
                    <div className="h4 mb-0 fw-bold">{leaveAllocations.length}</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-4">
                <div className="lp-stat-card d-flex align-items-center gap-3">
                  <div className="lp-stat-icon bg-success bg-opacity-10 text-success">
                    <FaIcons.FaCheckCircle />
                  </div>
                  <div>
                    <div className="text-muted small">Active Policies</div>
                    <div className="h4 mb-0 fw-bold">{activeCount}</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-4">
                <div className="lp-stat-card d-flex align-items-center gap-3">
                  <div className="lp-stat-icon bg-warning bg-opacity-10 text-warning">
                    <FaIcons.FaFileAlt />
                  </div>
                  <div>
                    <div className="text-muted small">Document Required</div>
                    <div className="h4 mb-0 fw-bold">{docRequiredCount}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="lp-section-card mb-4">
            <div className="lp-section-header d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                  <FaIcons.FaLayerGroup className="text-primary" />
                  Leave Allocation Management
                </h5>
                <p className="text-muted small mb-0">
                  Set maximum leave days per branch and leave type
                </p>
              </div>
              <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handleAddNew}>
                <FaIcons.FaPlus />
                Add Allocation
              </button>
            </div>
          </div>

          <div className="lp-section-card">
            <div className="lp-section-header">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <h6 className="fw-semibold mb-0">Browse Allocations</h6>
                  <p className="text-muted small mb-0">
                    {branchFiltered.length} record{branchFiltered.length !== 1 ? "s" : ""} · grouped by {viewMode === "branch" ? "branch" : viewMode === "leave_type" ? "leave type" : "list"}
                  </p>
                </div>
                <div className="lp-view-toggle">
                  <button
                    type="button"
                    className={viewMode === "branch" ? "active" : ""}
                    onClick={() => setViewMode("branch")}
                  >
                    <FaIcons.FaBuilding className="me-1" />
                    By Branch
                  </button>
                  <button
                    type="button"
                    className={viewMode === "leave_type" ? "active" : ""}
                    onClick={() => setViewMode("leave_type")}
                  >
                    <FaIcons.FaTags className="me-1" />
                    By Leave Type
                  </button>
                  <button
                    type="button"
                    className={viewMode === "all" ? "active" : ""}
                    onClick={() => setViewMode("all")}
                  >
                    <FaIcons.FaList className="me-1" />
                    All List
                  </button>
                </div>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                {viewMode !== "leave_type" && uniqueBranches.length > 0 && (
                  <div className="lp-branch-filter">
                    <button
                      type="button"
                      className={`lp-branch-chip ${branchFilter === "all" ? "active" : ""}`}
                      onClick={() => setBranchFilter("all")}
                    >
                      All Branches
                    </button>
                    {uniqueBranches.map((branch) => (
                      <button
                        key={branch}
                        type="button"
                        className={`lp-branch-chip ${branchFilter === branch ? "active" : ""}`}
                        onClick={() => setBranchFilter(branch)}
                      >
                        {branch}
                      </button>
                    ))}
                  </div>
                )}
                <div className="input-group ms-auto" style={{ maxWidth: 260 }}>
                  <span className="input-group-text bg-white border-end-0">
                    <FaIcons.FaSearch className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="p-3" style={{ background: "#f8fafc" }}>
              {showSkeleton ? (
                <div className="d-flex flex-column gap-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="lp-branch-group">
                      <div className="lp-branch-group-header">
                        <div className="lp-skeleton" style={{ width: 180, height: 20 }} />
                      </div>
                      <div className="lp-branch-group-body">
                        {[1, 2].map((j) => (
                          <div key={j} className="lp-alloc-row">
                            <div className="lp-skeleton" style={{ width: 120, height: 16 }} />
                            <div className="lp-skeleton" style={{ width: 40, height: 24 }} />
                            <div className="lp-skeleton" style={{ width: 36, height: 20 }} />
                            <div className="lp-skeleton" style={{ width: 44, height: 20 }} />
                            <div className="lp-skeleton" style={{ width: 28, height: 28 }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : branchFiltered.length === 0 ? (
                <div className="lp-empty-state py-4">
                  <div className="empty-icon">
                    <FaIcons.FaLayerGroup size={24} />
                  </div>
                  <h6 className="fw-semibold">
                    {searchTerm || branchFilter !== "all"
                      ? "No matching allocations"
                      : "No leave allocations yet"}
                  </h6>
                  <p className="text-muted small mb-0">
                    {searchTerm || branchFilter !== "all"
                      ? "Try a different filter or search term."
                      : 'Click "Add Allocation" to create your first policy.'}
                  </p>
                </div>
              ) : viewMode === "branch" ? (
                renderGroupedView(branchGroups, <FaIcons.FaBuilding />)
              ) : viewMode === "leave_type" ? (
                renderGroupedView(leaveTypeGroups, <FaIcons.FaTags />)
              ) : (
                <div className="lp-branch-group">
                  <div className="lp-branch-group-body">
                    {branchFiltered.map(renderAllocationRow)}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* model for creating and editing the leave allocation */}
          {openModal && (
            <>
              <div
                className="modal-backdrop fade show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1040,
                }}
                onClick={() => setOpenModal(false)}
              />
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                style={{ zIndex: 1050 }}
              >
                <div
                  className="modal-dialog modal-dialog-centered modal-lg"
                  style={{ maxWidth: 700 }}
                >
                  <div
                    className="modal-content border-0 rounded-4 shadow"
                    style={{ overflow: "hidden" }}
                  >
                    <div
                      className="modal-header"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title" style={{ fontWeight: 700 }}>
                          {editLeaveAllocation
                            ? "Edit Allocation"
                            : "Add Allocation"}
                        </h5>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setOpenModal(false)}
                      />
                    </div>
                    {/* modal body */}
                    <div className="modal-body py-3 px-4">
                      <div className="d-flex flex-column gap-3">
                        {/* branch and leave type */}
                        <div className="d-flex gap-3">
                          <div style={{ flex: 1 }}>
                            <label className="form-label mb-1">
                              Select Branch{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${
                                errors.branch_id ? "is-invalid" : ""
                              }`}
                              value={data.branch_id}
                              onChange={(e) =>
                                setData({ ...data, branch_id: e.target.value })
                              }
                            >
                              <option value="">-- Select Branch --</option>
                              {branches.map((branch) => (
                                <option key={branch.id} value={branch.id}>
                                  {branch.name}
                                </option>
                              ))}
                            </select>
                            {errors.branch_id && (
                              <div className="invalid-feedback d-block">
                                {errors.branch_id}
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="form-label mb-1">
                              Leave Type <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${
                                errors.leave_type_id ? "is-invalid" : ""
                              }`}
                              value={data.leave_type_id}
                              onChange={(e) =>
                                setData({
                                  ...data,
                                  leave_type_id: e.target.value,
                                })
                              }
                            >
                              <option value="">-- Select Leave Type --</option>
                              {leaveTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </select>
                            {errors.leave_type_id && (
                              <div className="invalid-feedback d-block">
                                {errors.leave_type_id}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* year and maximum days */}
                        <div className="d-flex gap-3">
                          <div style={{ flex: 1 }}>
                            <label className="form-label mb-1">
                              Max Days <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className={`form-select ${
                                errors.max_days ? "is-invalid" : ""
                              }`}
                              value={data.max_days}
                              onChange={(e) =>
                                setData({ ...data, max_days: e.target.value })
                              }
                              min="0"
                            />
                            {errors.max_days && (
                              <div className="invalid-feedback d-block">
                                {errors.max_days}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* required document and is active toggle */}
                        <div className="d-flex gap-3">
                          <div style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-2">
                              <label className="form-check-label mb-0">
                                Requires Document
                              </label>
                              <div className="form-check form-switch m-0">
                                <input
                                  style={{ width: "40px", height: "20px" }}
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={data.requires_document}
                                  onChange={(e) =>
                                    setData({
                                      ...data,
                                      requires_document: e.target.checked,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-2">
                              <label className="form-check-label mb-0">
                                Active
                              </label>
                              <div className="form-check form-switch m-0">
                                <input
                                  style={{ width: "40px", height: "20px" }}
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={data.is_active}
                                  onChange={(e) =>
                                    setData({
                                      ...data,
                                      is_active: e.target.checked,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* modal footer */}
                    <div
                      className="modal-footer"
                      style={{
                        background: "#f7f8fa",
                        borderTop: "1px solid #eef2f6",
                      }}
                    >
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setOpenModal(false)}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            {editLeaveAllocation
                              ? "Edit Allocation"
                              : "Save Allocation"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default LeaveAllocation;
